import { Effect } from 'effect';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mergeDocumentAssetPlugins } from '../assets';
import { OkraClient } from '../client';
import { createOkra } from '../providers';
import type { DocumentConfigUpdate, DocumentStatus, OkraSession, UploadInput } from '../types';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import type { OkraDocumentStatus, UseOkraDocumentReturn } from './types';
import { fetchDocumentStatusEffect } from './use-document-status';
import { useRequestOwner } from './use-request-owner';

const READY_PHASES = new Set(['complete', 'awaiting_review']);
const DOCUMENT_POLL_MS = 2000;

export type ProviderSource =
  | { kind: 'file'; input: UploadInput }
  | { kind: 'url'; input: string }
  | { kind: 'document'; documentId: string };

/**
 * Native session-resolution program — `documentId` attaches, `file`/`url`
 * upload. The failure is the redacted {@link OkraHookError}; upload bytes and
 * config payloads never enter it, and the original rejection is restored by the
 * Promise adapter so the provider's `catch` keeps its historical `Error`.
 */
export function resolveProviderSessionEffect(
  client: OkraClient,
  source: ProviderSource,
  uploadConfig?: DocumentConfigUpdate,
): Effect.Effect<OkraSession, OkraHookError> {
  if (source.kind === 'document') {
    return Effect.try({
      try: () => client.sessions.from(source.documentId),
      catch: (cause) =>
        okraHookFailure('providerSession', 'Document session could not be attached', cause),
    });
  }
  if ((client as unknown) instanceof OkraClient) {
    return client
      .createSessionEffect(source.input, {
        wait: false,
        upload: uploadConfig ? { config: uploadConfig } : undefined,
      })
      .pipe(
        Effect.mapError((error) =>
          okraHookFailure('providerSession', 'Document session could not be created', error.cause),
        ),
      );
  }
  return Effect.tryPromise({
    try: (signal) =>
      client.sessions.create(source.input, {
        wait: false,
        upload: { ...(uploadConfig ? { config: uploadConfig } : {}), signal },
      }),
    catch: (cause) =>
      okraHookFailure('providerSession', 'Document session could not be created', cause),
  });
}

/**
 * Native asset-plugin reconciliation: read the document spec, merge the assets
 * the mounted hooks registered, and write it back only when it changed.
 */
export function ensureDocumentAssetPluginsEffect(
  client: OkraClient,
  documentId: string,
  requestedAssets: readonly string[],
  options: { readonly isDisposed?: () => boolean } = {},
): Effect.Effect<boolean, OkraHookError> {
  return Effect.gen(function* () {
    const readFailure = (cause: unknown) =>
      okraHookFailure('assetPlugins', 'Document config could not be read', cause);
    const current = yield* (client as unknown) instanceof OkraClient
      ? client
          .getConfigEffect(documentId)
          .pipe(Effect.mapError((error) => readFailure(error.cause)))
      : Effect.tryPromise({
          try: (signal) => client.getConfig(documentId, signal),
          catch: readFailure,
        });

    // The React caller unmounting between the read and the write still skips
    // the write, exactly as the hand-rolled version did.
    if (options.isDisposed?.()) return false;

    const merged = yield* Effect.try({
      try: () => mergeDocumentAssetPlugins(current.spec, requestedAssets),
      catch: (cause) =>
        okraHookFailure('assetPlugins', 'Document config could not be merged', cause),
    });
    if (!merged.changed) return false;

    const writeFailure = (cause: unknown) =>
      okraHookFailure('assetPlugins', 'Document config could not be updated', cause);
    yield* (client as unknown) instanceof OkraClient
      ? client
          .updateConfigEffect(documentId, merged.spec)
          .pipe(Effect.mapError((error) => writeFailure(error.cause)))
      : Effect.tryPromise({
          try: (signal) => client.updateConfig(documentId, merged.spec, signal),
          catch: writeFailure,
        });
    return true;
  });
}

/** Keep the original readiness phases while giving status reads and poll sleeps one lifetime. */
export function pollProviderStatusEffect(
  session: OkraSession,
  onStatus: (status: DocumentStatus) => void,
): Effect.Effect<void, OkraHookError> {
  return Effect.gen(function* () {
    while (true) {
      const status = yield* fetchDocumentStatusEffect(session);
      yield* Effect.sync(() => onStatus(status));
      if (READY_PHASES.has(status.phase)) return;
      yield* Effect.sleep(DOCUMENT_POLL_MS);
    }
  });
}

export interface OkraContextValue extends UseOkraDocumentReturn {
  client: OkraClient;
  apiKey?: string;
  registerAsset: (assetId: string) => void;
}

const OkraContext = createContext<OkraContextValue | null>(null);

type OkraClientAuthProps =
  | {
      client: OkraClient;
      apiKey?: never;
      baseUrl?: never;
    }
  | {
      client?: never;
      apiKey: string;
      baseUrl?: string;
    };

type OkraProviderSourceProps =
  | {
      file: UploadInput;
      url?: never;
      documentId?: never;
      config?: DocumentConfigUpdate;
    }
  | {
      url: string;
      file?: never;
      documentId?: never;
      config?: DocumentConfigUpdate;
    }
  | {
      documentId: string;
      file?: never;
      url?: never;
      config?: never;
    };

export type OkraProviderProps = OkraClientAuthProps &
  OkraProviderSourceProps & {
    children: React.ReactNode;
  };

export function OkraProvider({
  client: providedClient,
  apiKey,
  baseUrl,
  file,
  url,
  documentId,
  config,
  children,
}: OkraProviderProps) {
  validateOkraProviderProps({ providedClient, apiKey, baseUrl, file, url, documentId, config });

  const client = useMemo<OkraClient>(() => {
    if (providedClient) return providedClient;
    return createOkra({ apiKey: apiKey!, baseUrl });
  }, [providedClient, apiKey, baseUrl]);

  const source = useMemo<ProviderSource>(
    () => resolveProviderSource({ file, url, documentId }),
    [file, url, documentId],
  );

  const configKey = useMemo(() => JSON.stringify(config ?? null), [config]);
  const uploadConfig = useMemo(
    () =>
      source.kind === 'document' || configKey === 'null'
        ? undefined
        : (JSON.parse(configKey) as DocumentConfigUpdate),
    [configKey, source.kind],
  );

  const [requestedAssets, setRequestedAssets] = useState<string[]>([]);
  const [reloadToken, setReloadToken] = useState(0);
  const selection = useMemo(() => ({}), [client, source, uploadConfig, reloadToken]);
  const owner = useRequestOwner(selection, true);
  const [state, setState] = useState<{
    selection: object;
    session: OkraSession | null;
    status: OkraDocumentStatus;
    error: Error | null;
    documentStatus: DocumentStatus | null;
  }>({ selection, session: null, status: 'resolving', error: null, documentStatus: null });
  const ensuredAssetKeys = useRef(new WeakMap<object, string>());
  const current =
    state.selection === selection
      ? state
      : {
          selection,
          session: null,
          status: 'resolving' as const,
          error: null,
          documentStatus: null,
        };
  const session = current.session;
  const assetsKey = JSON.stringify(requestedAssets.slice().sort());
  const assetSelection = useMemo(() => ({}), [selection, session, assetsKey]);
  const assetOwner = useRequestOwner(assetSelection, true);

  const registerAsset = useCallback((assetId: string) => {
    const normalized = assetId.trim();
    if (!normalized) return;
    setRequestedAssets((current) =>
      current.includes(normalized) ? current : [...current, normalized],
    );
  }, []);

  const refetch = useCallback(() => {
    if (!owner.begin()) return;
    assetOwner.begin();
    setReloadToken((current) => current + 1);
  }, [owner, assetOwner]);

  useEffect(() => {
    const request = owner.begin();
    if (!request) return;
    const isCurrent = () => owner.owns(request);
    setState({ selection, session: null, status: 'resolving', error: null, documentStatus: null });
    void runOkraHookEffect(
      Effect.gen(function* () {
        const session = yield* resolveProviderSessionEffect(client, source, uploadConfig);
        yield* Effect.sync(() => {
          if (isCurrent())
            setState({ selection, session, status: 'ready', error: null, documentStatus: null });
        });
        yield* pollProviderStatusEffect(session, (documentStatus) => {
          if (isCurrent()) setState((current) => ({ ...current, documentStatus }));
        });
      }),
      request.signal,
    ).catch((cause) => {
      if (isCurrent())
        setState((current) => ({
          ...current,
          status: 'error',
          error: cause instanceof Error ? cause : new Error(String(cause)),
        }));
    });
  }, [client, source, uploadConfig, selection, owner]);

  useEffect(() => {
    if (
      !session ||
      requestedAssets.length === 0 ||
      ensuredAssetKeys.current.get(selection) === assetsKey
    )
      return;
    const request = assetOwner.begin();
    if (!request) return;
    const isCurrent = () => assetOwner.owns(request);
    void runOkraHookEffect(
      ensureDocumentAssetPluginsEffect(client, session.id, requestedAssets, {
        isDisposed: () => !isCurrent(),
      }),
      request.signal,
    )
      .then(() => {
        if (isCurrent()) ensuredAssetKeys.current.set(selection, assetsKey);
      })
      .catch((cause) => {
        if (isCurrent())
          setState((current) => ({
            ...current,
            error: cause instanceof Error ? cause : new Error(String(cause)),
          }));
      });
  }, [client, requestedAssets, session, selection, assetsKey, assetOwner]);

  const documentIdValue = session?.id ?? (source.kind === 'document' ? source.documentId : null);
  const isReady = current.documentStatus ? READY_PHASES.has(current.documentStatus.phase) : false;
  const value = useMemo<OkraContextValue>(
    () => ({
      client,
      apiKey: apiKey ?? undefined,
      session,
      documentId: documentIdValue,
      status: current.status,
      error: current.error,
      isReady,
      refetch,
      registerAsset,
    }),
    [
      apiKey,
      client,
      documentIdValue,
      current.error,
      isReady,
      refetch,
      registerAsset,
      session,
      current.status,
    ],
  );

  return createElement(OkraContext.Provider, { value }, children);
}

export function useOkra(): OkraContextValue {
  const ctx = useContext(OkraContext);
  if (!ctx) {
    throw new Error('useOkra must be used within an <OkraProvider>');
  }
  return ctx;
}

export function useOkraDocument(): UseOkraDocumentReturn {
  const { documentId, session, status, error, isReady, refetch } = useOkra();
  return {
    documentId,
    session,
    status,
    error,
    isReady,
    refetch,
  };
}

function validateOkraProviderProps(options: {
  providedClient?: OkraClient;
  apiKey?: string;
  baseUrl?: string;
  file?: UploadInput;
  url?: string;
  documentId?: string;
  config?: DocumentConfigUpdate;
}): void {
  if (options.providedClient && (options.apiKey || options.baseUrl)) {
    throw new Error('OkraProvider accepts either `client` or `apiKey`/`baseUrl`, not both.');
  }
  if (!options.providedClient && !options.apiKey) {
    throw new Error('OkraProvider requires either a `client` or an `apiKey`.');
  }

  const providedSources = [
    options.file !== undefined,
    typeof options.url === 'string' && options.url.trim() !== '',
    typeof options.documentId === 'string' && options.documentId.trim() !== '',
  ].filter(Boolean).length;

  if (providedSources !== 1) {
    throw new Error('OkraProvider requires exactly one of `file`, `url`, or `documentId`.');
  }

  if (
    typeof options.documentId === 'string' &&
    options.documentId.trim() !== '' &&
    options.config !== undefined
  ) {
    throw new Error(
      'OkraProvider only accepts `config` when creating a document from `file` or `url`.',
    );
  }
}

function resolveProviderSource(options: {
  file?: UploadInput;
  url?: string;
  documentId?: string;
}): ProviderSource {
  if (options.file !== undefined) {
    return { kind: 'file', input: options.file };
  }
  if (typeof options.url === 'string' && options.url.trim() !== '') {
    return { kind: 'url', input: options.url.trim() };
  }
  if (typeof options.documentId === 'string' && options.documentId.trim() !== '') {
    return { kind: 'document', documentId: options.documentId.trim() };
  }
  throw new Error('OkraProvider requires exactly one of `file`, `url`, or `documentId`.');
}
